import { useCallback, useEffect, useRef, useState } from 'react';
import { physicalKey } from '../../net/keyboard';

/**
 * The interaction-menu controller (hotkeys → menus → type-a-number selection).
 *
 * One UNIVERSAL mechanism shared by every quick-action menu in the action bar (Emoji · Sounds ·
 * Animations · Hold). The number keys no longer fire anything directly: they OPEN a menu, then become a
 * little numeric keypad for picking an item out of it. The flow, end to end:
 *
 *   • a hotkey (1–4) opens the matching menu — opening one closes any other (only ever ONE open);
 *   • while a menu is open, the digit keys feed a buffer instead of doing anything else;
 *   • each typed digit (re)starts a {@link DIGIT_COMMIT_MS} timer, so multi-digit numbers (e.g. 32) can
 *     be typed at a human pace;
 *   • when the timer elapses the buffer is committed — the menu's registered `select(n)` runs and the
 *     menu closes.
 *
 * Menus stay decoupled: each one REGISTERS a {@link MenuSelector} (how many items it has + how to pick
 * the n-th). The controller knows nothing about emojis or sounds — so a brand-new menu joins the system
 * by registering a selector and claiming a hotkey, with zero changes here. (Behavioural Psychologist +
 * Principal Architect: one calm, predictable rule the player learns once and an open seam we can grow.)
 */

/** A menu's identity. A string (not a closed union) so new menus slot in without touching the core. */
export type MenuId = string;

/** How a menu participates in type-a-number selection. Numbering is 1-based: item N lives at index N-1. */
export interface MenuSelector {
  /** Count of selectable items (so the controller could bound-check / show hints if it wants). */
  count: number;
  /** Pick the 1-based n-th item. Out-of-range numbers are the menu's call to ignore. */
  select: (n: number) => void;
}

/** The public surface menus + the HUD use to drive the system. */
export interface InteractionMenus {
  /** The currently open menu, or null when everything's tucked away (only ever one is open). */
  open: MenuId | null;
  /** Digits typed so far in the pending selection (''=none) — surfaced so a menu can echo "typing 3…". */
  buffer: string;
  /** Open a menu (closing whatever was open + clearing any half-typed number). */
  openMenu: (id: MenuId) => void;
  /** Close the open menu (clearing the buffer). Safe to call when nothing is open. */
  closeMenu: () => void;
  /** Open `id` if it's closed, else close it — the click behaviour for a menu's toggle button. */
  toggleMenu: (id: MenuId) => void;
  /** A menu registers/updates how it selects by number. Cheap (no re-render); call it from an effect. */
  registerSelector: (id: MenuId, selector: MenuSelector) => void;
}

/** Idle window after the last digit before the buffer commits — long enough to type a two-digit number. */
export const DIGIT_COMMIT_MS = 1000;

/**
 * Map a key event to a digit char ('0'–'9'), or null. Resolved by PHYSICAL key position (via
 * {@link physicalKey}), not the produced character, so the number row + numpad select items on every
 * layout — AZERTY's top row yields `&é"…` from `e.key`, but Digit1 is still '1'. Synthetic events with
 * no `code` fall back to `e.key`, so dispatched tests keep working.
 */
function digitOf(e: KeyboardEvent): string | null {
  const k = physicalKey(e);
  return k >= '0' && k <= '9' ? k : null;
}

/**
 * True when the event target is a text-entry control, so the OPEN hotkeys don't fire while the player is
 * typing (chat, the mic `<select>`, …). Mirrors the guard in net/input.ts. Note: this gates only the
 * menu-OPEN keys; once a menu is open, digits are intercepted regardless of focus (the spec: while a menu
 * is open, number keys ONLY select), so the player can never half-type a number into a stray field.
 */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/**
 * Wire up the controller + its single window keydown listener.
 *
 * @param hotkeys  digit-char → menu id, e.g. `{ '1': 'emoji', '2': 'sound' }`. Pass a STABLE reference
 *                 (a module-level constant) — the listener re-binds if it changes.
 */
export function useInteractionMenus(hotkeys: Readonly<Record<string, MenuId>>): InteractionMenus {
  const [open, setOpen] = useState<MenuId | null>(null);
  const [buffer, setBuffer] = useState('');

  // Selectors live in a ref (a mutable registry) — registering must never trigger a re-render, and the
  // timer/keyboard callbacks read the latest entry without being in any dependency array.
  const selectors = useRef(new Map<MenuId, MenuSelector>()).current;
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mirror the latest open menu + buffer into refs so the commit timer and the (once-bound) window
  // listener always see current values without re-subscribing on every keystroke.
  const openRef = useRef<MenuId | null>(null);
  const bufferRef = useRef('');
  openRef.current = open;
  bufferRef.current = buffer;

  const clearTimer = useCallback((): void => {
    if (commitTimer.current != null) {
      clearTimeout(commitTimer.current);
      commitTimer.current = null;
    }
  }, []);

  const closeMenu = useCallback((): void => {
    clearTimer();
    setBuffer('');
    setOpen(null);
  }, [clearTimer]);

  const openMenu = useCallback((id: MenuId): void => {
    clearTimer();
    setBuffer('');
    setOpen(id);
  }, [clearTimer]);

  const toggleMenu = useCallback((id: MenuId): void => {
    clearTimer();
    setBuffer('');
    setOpen((prev) => (prev === id ? null : id));
  }, [clearTimer]);

  const registerSelector = useCallback((id: MenuId, selector: MenuSelector): void => {
    selectors.set(id, selector);
  }, [selectors]);

  // Commit the buffered number: close the menu (the world goes calm again) then run its selection. A menu
  // is responsible for ignoring an out-of-range number — committing always closes, so a fat-fingered "99"
  // dismisses cleanly rather than getting stuck.
  const commit = useCallback((): void => {
    clearTimer();
    const id = openRef.current;
    const raw = bufferRef.current;
    setBuffer('');
    setOpen(null);
    if (id == null || raw === '') return;
    const n = Number.parseInt(raw, 10);
    if (!Number.isNaN(n)) selectors.get(id)?.select(n);
  }, [clearTimer, selectors]);

  // The ONE window listener for the whole system. Capture phase so digits are intercepted before any
  // focused field (search box, chat) can consume them — while a menu is open, digits ONLY select.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (openRef.current != null) {
        const d = digitOf(e);
        if (d != null && !e.repeat) {
          // While a menu is open the digits belong to the buffer and nothing else.
          e.preventDefault();
          e.stopPropagation();
          setBuffer((prev) => prev + d);
          clearTimer();
          commitTimer.current = setTimeout(commit, DIGIT_COMMIT_MS);
        }
        // Other keys (Escape, letters into the sound search, …) pass through to the popover untouched.
        return;
      }
      // No menu open: a bare 1–4 opens its menu (but never while the player is typing in a field).
      // Keyed by physical position so the hotkeys survive non-US layouts (see physicalKey).
      if (e.repeat || isTyping(e.target)) return;
      const id = hotkeys[physicalKey(e)];
      if (id != null) {
        e.preventDefault();
        openMenu(id);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [hotkeys, clearTimer, commit, openMenu]);

  // Drop a pending commit on unmount so a fired timer can't touch an unmounted tree.
  useEffect(() => () => clearTimer(), [clearTimer]);

  return { open, buffer, openMenu, closeMenu, toggleMenu, registerSelector };
}
